import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  UNDERWAY_DELAY_MS,
  type UnderwayActivity,
  type UnderwayCandidate,
  type UnderwayStatus,
  type UnderwayViewMode,
} from './types';

export const buildGroupKey = (stageId: string, activityKey: string) =>
  `${stageId}:${activityKey}`;

type EnqueueInput = {
  groupKey: string;
  activityKind: string;
  activityLabel: string;
  stageId: string;
  stageName: string;
  jobId: string;
  jobName: string;
  candidates: UnderwayCandidate[];
  isBulk: boolean;
  executionIds: number[];
};

type UnderwayState = {
  activities: UnderwayActivity[];
  viewMode: UnderwayViewMode;
  // Mutations
  enqueue: (input: EnqueueInput) => void;
  pause: (id: string) => void;
  resume: (id: string) => void;
  setStatus: (
    id: string,
    status: UnderwayStatus,
    errorMessage?: string
  ) => void;
  dismiss: (id: string) => void;
  clearTerminal: () => void;
  // View mode
  setViewMode: (mode: UnderwayViewMode) => void;
};

const mergeIntoExisting = (
  existing: UnderwayActivity,
  next: EnqueueInput
): UnderwayActivity => {
  const seenCandidateIds = new Set(existing.candidates.map((c) => c.id));
  const mergedCandidates = [
    ...existing.candidates,
    ...next.candidates.filter((c) => !seenCandidateIds.has(c.id)),
  ];
  const seenExecIds = new Set(existing.executionIds);
  const mergedExecIds = [...existing.executionIds];
  next.executionIds.forEach((id) => {
    if (!seenExecIds.has(id)) {
      mergedExecIds.push(id);
      seenExecIds.add(id);
    }
  });
  return {
    ...existing,
    candidates: mergedCandidates,
    isBulk: existing.isBulk || next.isBulk,
    executionIds: mergedExecIds,
    createdAt: Date.now(),
    delayMs: UNDERWAY_DELAY_MS,
    remainingMs: UNDERWAY_DELAY_MS,
    pausedAt: null,
    status: 'pending',
    errorMessage: undefined,
  };
};

// Persisted to sessionStorage so a refresh during the 15s window
// recovers the popup and UnderwayTimerManager re-fires the still-pending
// executions (see `merge` below — recovered rows are normalised back to
// `pending` with elapsed-adjusted timing). Tab-close / crash are caught
// by the BE sweeper (`Workflow::StageAutomationSweepWorker`).
export const useUnderwayStore = create<UnderwayState>()(
  persist(
    (set) => ({
      activities: [],
      viewMode: 'expanded',

      enqueue: (input) =>
        set((state) => {
          const existing = state.activities.find(
            (a) => a.id === input.groupKey
          );
          if (existing) {
            return {
              ...state,
              activities: state.activities.map((a) =>
                a.id === input.groupKey ? mergeIntoExisting(a, input) : a
              ),
              viewMode:
                state.viewMode === 'hidden' ? 'expanded' : state.viewMode,
            };
          }
          const fresh: UnderwayActivity = {
            id: input.groupKey,
            activityKind: input.activityKind,
            activityLabel: input.activityLabel,
            stageId: input.stageId,
            stageName: input.stageName,
            jobId: input.jobId,
            jobName: input.jobName,
            candidates: input.candidates,
            isBulk: input.isBulk,
            executionIds: input.executionIds,
            createdAt: Date.now(),
            delayMs: UNDERWAY_DELAY_MS,
            remainingMs: UNDERWAY_DELAY_MS,
            pausedAt: null,
            status: 'pending',
          };
          return {
            ...state,
            activities: [...state.activities, fresh],
            viewMode: state.viewMode === 'hidden' ? 'expanded' : state.viewMode,
          };
        }),

      pause: (id) =>
        set((state) => ({
          ...state,
          activities: state.activities.map((a) => {
            if (a.id !== id || a.status !== 'pending') return a;
            const elapsed = Date.now() - a.createdAt;
            const remaining = Math.max(0, a.delayMs - elapsed);
            return {
              ...a,
              status: 'paused',
              remainingMs: remaining,
              pausedAt: Date.now(),
            };
          }),
        })),

      resume: (id) =>
        set((state) => ({
          ...state,
          activities: state.activities.map((a) => {
            if (a.id !== id || a.status !== 'paused') return a;
            return {
              ...a,
              status: 'pending',
              createdAt: Date.now() - (a.delayMs - a.remainingMs),
              pausedAt: null,
            };
          }),
        })),

      setStatus: (id, status, errorMessage) =>
        set((state) => ({
          ...state,
          activities: state.activities.map((a) =>
            a.id === id ? { ...a, status, errorMessage } : a
          ),
        })),

      dismiss: (id) =>
        set((state) => ({
          ...state,
          activities: state.activities.filter((a) => a.id !== id),
        })),

      clearTerminal: () =>
        set((state) => ({
          ...state,
          activities: state.activities.filter(
            (a) =>
              a.status === 'pending' ||
              a.status === 'paused' ||
              a.status === 'running'
          ),
        })),

      setViewMode: (mode) => set((state) => ({ ...state, viewMode: mode })),
    }),
    {
      name: 'kula-underway-popup',
      version: 1,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        activities: state.activities.filter(
          (a) =>
            a.status === 'pending' ||
            a.status === 'paused' ||
            a.status === 'running'
        ),
        viewMode: state.viewMode,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<
          Pick<UnderwayState, 'activities' | 'viewMode'>
        >;
        const now = Date.now();
        const activities = (p.activities ?? []).map((a) => {
          let createdAt = a.createdAt;
          if (a.status === 'paused') {
            createdAt = now - (a.delayMs - a.remainingMs);
          } else if (a.status === 'running') {
            createdAt = now - a.delayMs;
          }
          return {
            ...a,
            status: 'pending' as const,
            createdAt,
            remainingMs: a.delayMs,
            pausedAt: null,
            errorMessage: undefined,
          };
        });
        return {
          ...current,
          activities,
          viewMode: p.viewMode ?? current.viewMode,
        };
      },
    }
  )
);
